import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'formlabs-web-components/Link';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { GridContainer, Grid, Cell } from 'formlabs-web-components/Grid';
import { Picture } from 'formlabs-web-components/Media';
import { Eyebrow, Header } from 'formlabs-web-components/Type';
import useIsMobile from '../../../../common/hooks/useIsMobile';
import './Blog.module.css';
import SocialShareButtons from './SocialShareButtons';
import { TableOfContents } from '../../../../common/components/content';
import { compareWithLocale } from '../../../../common/utils';

const BlogPost = ({
  title,
  children,
  category,
  pubDate,
  type,
  hideDate,
  url,
  relatedPosts
}) => {
  const { t, i18n } = useTranslation();
  const [readTime, setReadTime] = useState(null);
  if (relatedPosts)
    relatedPosts.sort((a, b) =>
      compareWithLocale(a.title, b.title, i18n.language)
    );

  // calculate read time
  useEffect(
    () => {
      const textPlugins = document.getElementsByClassName('text-plugin');
      const text = [...textPlugins].reduce(
        (result, element) => result + element.innerText,
        ''
      );
      const wordCount = text.split(' ').length;

      let minutes = Math.floor(parseFloat(wordCount / 200));
      let seconds = ((wordCount / 200).toFixed(2) + '').split('.');
      seconds = Math.floor(parseFloat(seconds[1] * 0.6).toFixed(2));

      if (seconds > 30) {
        minutes++;
      }
      if (minutes < 1) {
        minutes++;
        minutes = '< ' + minutes;
      }
      setReadTime(minutes);
    },
    [children]
  );

  const refined_url =
    url.slice(0, 6) === '/blog/' ? '/blog/' : url.slice(0, 4) + 'blog/';

  return (
    <div styleName="single-post-wrapper">
      <GridContainer>
        <Grid direction="x">
          <Cell largeSize={24}>
            <Link to={refined_url} styleName="all-posts">
              <i className="fl-arrow-left" />
              {t('All Posts')}
            </Link>
            <div styleName="post-type">
              <Eyebrow>{type}</Eyebrow>
            </div>
          </Cell>
          <Cell largeSize={18}>
            <Header level={1} displayAs={1} weight="600">
              {title}
            </Header>
          </Cell>
          <Cell largeSize={6}>
            <SocialShareButtons title={title} type={type} url={url} />
          </Cell>
        </Grid>
      </GridContainer>
      <GridContainer>
        {((!hideDate && pubDate) || category || type || readTime) && (
          <div styleName="byline">
            {!hideDate && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-calendar" />
                </div>
                <div styleName="label">{pubDate}</div>
              </div>
            )}
            {category && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-offer" />
                </div>
                <div styleName="label">{category}</div>
              </div>
            )}
            {type && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-folder_open" />
                </div>
                <div styleName="label">{type}</div>
              </div>
            )}
            {readTime && (
              <div styleName="item">
                <div styleName="icon">
                  <i className="fl-schedule" />
                </div>
                <div styleName="label">
                  {t('{{measure}} minute read', {
                    measure: readTime
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </GridContainer>
      <TableOfContents levels={[2, 3]}>{children}</TableOfContents>
      <GridContainer>
        <Grid direction="x" primaryAlignment="align-left">
          {relatedPosts.length > 0 && (
            <Cell smallSize={24}>
              <div styleName="related-posts">
                <Header level={3} displayAs={4}>
                  {t('Related Posts')}
                </Header>
                <Grid direction="x" gutters="padding">
                  {relatedPosts.map(post => (
                    <Cell mediumSize={8} key={post.title}>
                      <div styleName="related-single-post">
                        <Link to={post.url}>
                          <Picture largeImage={post.listing_image} />
                          {post.category && (
                            <Eyebrow>{post.category.name}</Eyebrow>
                          )}
                          <p>{post.title}</p>
                        </Link>
                      </div>
                    </Cell>
                  ))}
                </Grid>
              </div>
            </Cell>
          )}
        </Grid>
      </GridContainer>
    </div>
  );
};

export default DisplayComponent(BlogPost);
